/**
 * Money, formatted the handful of ways this app actually shows it.
 *
 * One module because there were five private `money` helpers scattered across
 * the CRM, two of them character-for-character identical -- including the
 * `try/catch` that stops an unrecognised ISO currency code blanking a whole
 * panel. Named exports rather than one clever function with options: the
 * variants differ in what they are *for*, not in a parameter, and a caller
 * choosing `compactCurrency` over `currency` is making the decision that
 * matters at the point where it is obvious which one they want.
 */

/**
 * A figure with its currency symbol, to the nearest whole unit.
 *
 * Whole units because these are company accounts -- turnover and shareholders'
 * funds are reported in thousands and nobody reads the pence.
 *
 * Falls back to "<code> <number>" when `Intl` does not recognise the code.
 * Creditsafe returns whatever the filing used, so an unusual currency is a
 * normal answer rather than an error -- and throwing here would take out the
 * entire financials panel over a symbol. The figure is still the figure.
 */
export function currency(
  value: number | null | undefined,
  currencyCode: string,
): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currencyCode} ${new Intl.NumberFormat("en-GB", {
      maximumFractionDigits: 0,
    }).format(value)}`;
  }
}

/**
 * Sterling, shortened for a narrow column.
 *
 * "£12.3M" is comparable down a column of five other numbers in a way that
 * "£12,345,678" is not. The exact figure is on the company page.
 */
export function compactCurrency(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** Sterling to the nearest pound, for a sentence rather than a table. */
export function pounds(amount: number): string {
  return `£${Math.round(amount).toLocaleString("en-GB")}`;
}
